import { useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { URL_VARS } from '../../constants/routing';
import { PER_PAGE_OPTIONS } from '../../constants/table';
import useClearFiltersCallback from '../../hooks/useClearFiltersCallback';
import useWindowSize from '../../hooks/useWindowSize';
import { IFiltersRef } from '../../types/tables';
import { TCollectiveEmailTemplate, TCollectiveTemplateItem } from '../../types/templates';
import { getEmailTemplateLabelByType, isTemplateEmail } from '../../utils/templates';
import Card from '../Card/Card';
import { ReportsStatus } from '../index';
import PaginatedList, { IPaginatedListChangeEvent } from '../PaginatedList/PaginatedList';
import {
  PageContent,
  PaginationControls,
  TableEmptyPlaceholder,
  TableFilteredEmptyPlaceholder,
  TableViewLoader,
} from '../_mui';
import { useTemplateDepartments } from './hooks/useTemplateDepartments';
import { useTemplateListLogic } from './hooks/useTemplateListLogic';
import { useTemplatePermissions } from './hooks/useTemplatePermissions';

import styles from './templates.module.scss';

interface IProps {
  templates: TCollectiveTemplateItem[];
  onEdit: (template: TCollectiveTemplateItem) => void;
  onDelete: (template: TCollectiveTemplateItem) => void;
  isLoading?: boolean;
  filtersRef?: React.RefObject<IFiltersRef>;
  totalCount: number;
  pageSize: number;
  page?: number;
  onChange: (event: IPaginatedListChangeEvent) => void;
  onPageSizeChange: (newPageSize: number) => void;
  hasFiltersApplied?: boolean;
}

export const EmailTemplatesList: React.FC<IProps> = ({
  templates,
  onEdit,
  onDelete,
  isLoading = false,
  filtersRef,
  totalCount,
  pageSize,
  page,
  onChange,
  onPageSizeChange,
  hasFiltersApplied,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const { mobile } = useWindowSize();
  const { sortedTemplates, isEmpty } = useTemplateListLogic(templates);
  const { getDepartmentsInfo, renderDefaultLabel } = useTemplateDepartments();
  const { renderControls } = useTemplatePermissions(onEdit, onDelete);
  const handleClearFilters = useClearFiltersCallback(filtersRef ?? null);

  const renderContent = useCallback(
    (templatesList: TCollectiveTemplateItem[]) => {
      return !templatesList || !templatesList.length ? (
        <TableFilteredEmptyPlaceholder itemType="email templates" onClearFilters={handleClearFilters} />
      ) : (
        <PageContent>
          <div className={styles.list}>
            {templatesList.map((template) => {
              const { item_id: id, templateType, is_default = false } = template;
              return (
                <Card
                  className={styles.card}
                  key={`${id}-${templateType}`}
                  top={
                    <>
                      <div className={styles.templateInfo}>
                        <span className={styles.title}>{(template as TCollectiveEmailTemplate).subject}</span>
                        <span className={styles.type}>{templateType}</span>
                        {isTemplateEmail(templateType) && (
                          <ReportsStatus
                            className={styles.emailType}
                            status={getEmailTemplateLabelByType((template as TCollectiveEmailTemplate).type)}
                            backgroundColor={theme.palette.warning.main}
                          />
                        )}
                      </div>
                      {getDepartmentsInfo(template, styles)}
                    </>
                  }
                  bottom={
                    <>
                      {mobile && renderDefaultLabel(is_default, styles)}
                      {renderControls(template, styles)}
                    </>
                  }
                  topClassName={styles.top}
                  middleClassName={styles.middle}
                  bottomClassName={styles.controlsWrapper}
                />
              );
            })}
          </div>
        </PageContent>
      );
    },
    [mobile, getDepartmentsInfo, renderDefaultLabel, renderControls, theme.palette.warning.main, handleClearFilters],
  );

  const emptyList = useMemo(() => !isLoading && isEmpty, [isEmpty, isLoading]);
  const hasItems = useMemo(() => !isLoading && !isEmpty, [isEmpty, isLoading]);

  return (
    <>
      {isLoading && <TableViewLoader />}
      {emptyList &&
        (hasFiltersApplied ? (
          <TableFilteredEmptyPlaceholder itemType="email templates" onClearFilters={handleClearFilters} />
        ) : (
          <TableEmptyPlaceholder
            item="email templates"
            subtitle="Create a template to reuse layout and content across email sends."
            buttonText="Create Template"
            onButtonClick={() => history.push(`/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EMAIL}/${URL_VARS.NEW}`)}
          />
        ))}
      {hasItems && (
        <PaginatedList<TCollectiveTemplateItem>
          items={sortedTemplates}
          pageSize={pageSize}
          totalCount={totalCount}
          isFinalItems={true}
          defaultValue={page}
          onChange={onChange}
        >
          {({ items: paginatedTemplates, pagination: Pagination }) => {
            const hasTemplates = paginatedTemplates.length > 0;

            return (
              <>
                {renderContent(paginatedTemplates)}
                {hasTemplates && (
                  <PaginationControls
                    pageSize={pageSize}
                    options={PER_PAGE_OPTIONS}
                    onPageSizeChange={onPageSizeChange}
                    PaginationComponent={Pagination}
                  />
                )}
              </>
            );
          }}
        </PaginatedList>
      )}
    </>
  );
};
